import { createEntityAdapter, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type DeepPartial } from "react-hook-form";
import { all } from "redux-saga/effects";

import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { getIds } from "@ds/utils/entities";
import { LoadStatus, type BaseMeta, type PatchedModel } from "@ds/utils/reducer";
import { type SagaOptions } from "@ds/utils/saga-helpers";

import { ExperienceTableTypeEnum } from "../utils/model";
import { apiToModelNormalize, normalize } from "../utils/normalizer";
import { watchExperiences } from "./sagas";

export enum ExperiencesRemoteOperation {
  CRUD = "crud",
  CHECKBOXES = "checkboxes",
  IOT_FETCH = "iot_fetch",
}

export type ExperiencesMeta = BaseMeta & {
  remoteOperation: ExperiencesRemoteOperation;
  filters?: KeysToCamelCase<ExperienceFilters>;
};

export interface ExperiencesState {
  viewType: ExperienceTableTypeEnum | undefined;
  loadStatusMap: {
    [ExperiencesRemoteOperation.CRUD]: LoadStatus;
    [ExperiencesRemoteOperation.CHECKBOXES]: LoadStatus;
    [ExperiencesRemoteOperation.IOT_FETCH]: LoadStatus;
  };

  error: string | null;

  entities: {
    [experienceId: number]: Experience;
  };

  ids: number[];
  tableIds: Record<string, number[]>;
  selectedIds: Record<string, number[]>;
}

const entityAdapter = createEntityAdapter<Experience>();

export const initialState: ExperiencesState = entityAdapter.getInitialState({
  viewType: undefined,
  loadStatusMap: {
    [ExperiencesRemoteOperation.CRUD]: LoadStatus.Idle,
    [ExperiencesRemoteOperation.CHECKBOXES]: LoadStatus.Idle,
    [ExperiencesRemoteOperation.IOT_FETCH]: LoadStatus.Idle,
  },

  error: null,

  tableIds: { [MainTableDataTypeEnum.Experiences]: [], [DetailsTableDataTypeEnum.LocationExperiences]: [] },
  selectedIds: { [MainTableDataTypeEnum.Experiences]: [], [DetailsTableDataTypeEnum.LocationExperiences]: [] },
}) as ExperiencesState;

const getTableTypeOrDefault = (
  tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum = MainTableDataTypeEnum.Experiences,
) => tableType;

const removeEntities = (state: ExperiencesState, payload: number[]) => {
  entityAdapter.removeMany(state, payload);

  state.tableIds[MainTableDataTypeEnum.Experiences] = state.tableIds[MainTableDataTypeEnum.Experiences].filter(
    id => !payload.includes(id),
  );

  state.tableIds[DetailsTableDataTypeEnum.LocationExperiences] = state.tableIds[
    DetailsTableDataTypeEnum.LocationExperiences
  ].filter(id => !payload.includes(id));

  state.selectedIds[MainTableDataTypeEnum.Experiences] = state.selectedIds[MainTableDataTypeEnum.Experiences].filter(
    id => !payload.includes(id),
  );

  state.selectedIds[DetailsTableDataTypeEnum.LocationExperiences] = state.selectedIds[
    DetailsTableDataTypeEnum.LocationExperiences
  ].filter(id => !payload.includes(id));
};

const sliceType = "EXPERIENCES";
const slice = createSlice({
  name: sliceType,
  initialState,
  reducers: {
    switchView: (state, { payload }: PayloadAction<ExperienceTableTypeEnum>) => {
      state.viewType = payload;
    },
    fetchExperiences: {
      reducer(state, { meta }: PayloadAction<undefined, string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;

        if (meta.remoteOperation === ExperiencesRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] =
            state.tableIds[getTableTypeOrDefault(meta.options?.tableType)];
        }
      },
      prepare(
        filters: KeysToCamelCase<ExperienceFilters> = {},
        options: SagaOptions = {},
        remoteOperation: ExperiencesRemoteOperation = ExperiencesRemoteOperation.CRUD,
      ) {
        return {
          payload: undefined,
          meta: { remoteOperation, filters, options },
        };
      },
    },
    fetchExperiencesFailed: {
      reducer(state, { meta, error }: PayloadAction<undefined, string, ExperiencesMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;

        if (meta.remoteOperation === ExperiencesRemoteOperation.CHECKBOXES) {
          state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = [];
        }
      },
      prepare(meta: ExperiencesMeta, { message }: ErrorLike) {
        return { payload: undefined, meta, error: message };
      },
    },
    fetchExperiencesSucceeded: {
      reducer(
        state,
        { payload, meta }: PayloadAction<QueryOutput<Experience> | QueryOutput<ApiExperience>, string, ExperiencesMeta>,
      ) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setMany(state, apiToModelNormalize(payload.items));

        if (meta.remoteOperation === ExperiencesRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = payload.items.map(({ id }) => id);
        } else if (meta.options?.tableType) {
          state.tableIds[getTableTypeOrDefault(meta.options?.tableType)] = payload.items.map(({ id }) => id);
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = state.selectedIds[
            getTableTypeOrDefault(meta.options?.tableType)
          ].filter(id => state.ids.includes(id));
        }
      },
      prepare(payload: QueryOutput<Experience> | QueryOutput<ApiExperience>, meta: ExperiencesMeta) {
        return { payload, meta };
      },
    },
    fetchExperience: {
      reducer(state, { meta }: PayloadAction<number, string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(payload: number, options: SagaOptions = {}) {
        return { payload, meta: { remoteOperation: ExperiencesRemoteOperation.CRUD, options } };
      },
    },
    fetchExperienceFailed: {
      reducer(state, { meta, error }: PayloadAction<number, string, ExperiencesMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: number, meta: ExperiencesMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    fetchExperienceSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<Experience | ApiExperience, string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setOne(state, apiToModelNormalize(payload));
      },
      prepare(payload: Experience | ApiExperience, meta: ExperiencesMeta) {
        return { payload, meta };
      },
    },
    createExperience: {
      reducer(state, { meta }: PayloadAction<Experience, string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(payload: Experience, meta: ExperiencesMeta = { remoteOperation: ExperiencesRemoteOperation.CRUD }) {
        return { payload, meta };
      },
    },
    createExperienceFailed: {
      reducer(state, { meta, error }: PayloadAction<Experience, string, ExperiencesMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: Experience, meta: ExperiencesMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    createExperienceSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<ApiExperience, string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setOne(state, apiToModelNormalize(payload));
      },
      prepare(payload: ApiExperience, meta: ExperiencesMeta) {
        return { payload, meta };
      },
    },
    updateExperiences: {
      reducer(state, _action: PayloadAction<PatchedModel<Experience>>) {
        state.loadStatusMap[ExperiencesRemoteOperation.CRUD] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(changedIds: number[], changedFields: DeepPartial<Experience>) {
        return { payload: { ids: changedIds, data: changedFields } };
      },
    },
    updateExperiencesFailed: {
      reducer(state, { error }: PayloadAction<PatchedModel<Experience>, string, never, string>) {
        state.loadStatusMap[ExperiencesRemoteOperation.CRUD] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: PatchedModel<Experience>, { message }: ErrorLike) {
        return { payload, error: message };
      },
    },
    updateExperiencesSucceeded: (state, { payload }: PayloadAction<ApiExperience[]>) => {
      state.loadStatusMap[ExperiencesRemoteOperation.CRUD] = LoadStatus.Succeeded;
      state.error = null;

      entityAdapter.setMany(state, apiToModelNormalize(payload));
    },
    deleteExperiences: {
      reducer(state, { meta }: PayloadAction<number[], string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(experiencesOrIds: Experience[] | number[], redirectTo?: string) {
        return {
          payload: getIds(experiencesOrIds),
          meta: { remoteOperation: ExperiencesRemoteOperation.CRUD, options: { redirectTo } },
        };
      },
    },
    deleteExperiencesFailed: {
      reducer(state, { meta, error }: PayloadAction<number[], string, ExperiencesMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(idsToDelete: number[], meta: ExperiencesMeta, { message }: ErrorLike) {
        return { payload: idsToDelete, meta, error: message };
      },
    },
    deleteExperiencesSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<number[], string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        removeEntities(state, payload);
      },
      prepare(payload: number[], meta: ExperiencesMeta) {
        return { payload, meta };
      },
    },
    selectExperiences: {
      reducer(state, { payload, meta }: PayloadAction<number[], string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;

        state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = payload;
      },
      prepare(experiencesOrIds: Experience[] | ApiExperience[] | number[], options: SagaOptions = {}) {
        return {
          payload: getIds(experiencesOrIds),
          meta: { remoteOperation: ExperiencesRemoteOperation.CHECKBOXES, options },
        };
      },
    },
    toggleSelected: {
      reducer(state, { payload, meta }: PayloadAction<number, string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;

        state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = state.selectedIds[
          getTableTypeOrDefault(meta.options?.tableType)
        ].includes(payload)
          ? state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)].filter(id => id !== payload)
          : [...state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)], payload];
      },
      prepare(experienceId: number, options: SagaOptions = {}) {
        return {
          payload: experienceId,
          meta: { remoteOperation: ExperiencesRemoteOperation.CHECKBOXES, options },
        };
      },
    },
    reorderExperiencePlayers: {
      reducer(state, { meta }: PayloadAction<{ experienceId: number; playerIds: number[] }, string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
      },
      prepare(payload: { experienceId: number; playerIds: number[] }) {
        return {
          payload,
          meta: {
            remoteOperation: ExperiencesRemoteOperation.CRUD,
          },
        };
      },
    },
    reorderExperiencePlayersSucceeded: {
      reducer(state, { meta }: PayloadAction<{ experienceId: number; playerIds: number[] }, string, ExperiencesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
      },
      prepare(payload: { experienceId: number; playerIds: number[] }, meta: ExperiencesMeta) {
        return { payload, meta };
      },
    },
    reorderExperiencePlayersFailed: {
      reducer(
        state,
        { meta, error }: PayloadAction<{ experienceId: number; playerIds: number[] }, string, ExperiencesMeta, string>,
      ) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: { experienceId: number; playerIds: number[] }, meta: ExperiencesMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    iotUpdate: (state, { payload }: PayloadAction<ApiExperience>) => {
      if (payload.audit?.deleted_at) {
        removeEntities(state, [payload.id]);
      } else {
        entityAdapter.setOne(state, apiToModelNormalize(normalize(payload)));
      }
    },
  },
});

export const { name: experiencesType, actions: experiencesActions, reducer: experiencesReducer } = slice;

export const entitySelectors = entityAdapter.getSelectors();

export function* rootExperiencesSaga() {
  yield all([watchExperiences()]);
}
